import React, { lazy } from "react";

type LazyWithPreload<T extends React.ComponentType<any>> =
  React.LazyExoticComponent<T> & {
    preload: () => Promise<{ default: T }>;
  };

// Timestamp (ms) marking the start of the current reload window.
export const CHUNK_RELOAD_KEY = "chunk-reload-ts";
// Number of reloads already triggered inside the current window.
export const CHUNK_RELOAD_COUNT_KEY = "chunk-reload-count";

// Allow a few reloads per window so multi-navigation flows (e.g. "take over"
// → redirect → reopen conversation) aren't blocked by a single earlier reload,
// while still capping the total to prevent an infinite reload loop when the
// fresh entry-point keeps failing.
const MAX_RELOADS_PER_WINDOW = 3;
const RELOAD_WINDOW_MS = 60_000;

// Rule #12: keep shareable patterns as strings; build the RegExp inside the
// function that uses them. These match the chunk-load failures thrown by the
// browser/Vite when a hashed chunk URL no longer exists after a deployment.
const CHUNK_ERROR_PATTERNS = [
  "dynamically imported module",
  "Loading chunk [\\w-]+ failed",
  "Unable to preload CSS",
];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === "object" && error !== null && "message" in error) {
    const { message } = error as { message: unknown };
    if (typeof message === "string") {
      return message;
    }
  }

  return String(error);
}

export function isChunkError(error: unknown): boolean {
  const message = getErrorMessage(error);

  return CHUNK_ERROR_PATTERNS.some((pattern) =>
    new RegExp(pattern, "i").test(message)
  );
}

/**
 * Triggers a full page reload after a chunk-load failure so the browser fetches
 * the freshly-deployed entry-point and chunk hashes.
 *
 * A windowed counter in sessionStorage allows up to MAX_RELOADS_PER_WINDOW
 * reloads per RELOAD_WINDOW_MS. This replaces the previous single-timestamp
 * guard, which surfaced a hard error whenever a second chunk failure happened
 * within 10s of the first reload (exactly the "take over → reopen" flow).
 *
 * Returns true if a reload was triggered, false once the budget is exhausted so
 * the caller can let the error propagate to the ErrorBoundary.
 */
export function reloadOnChunkError(): boolean {
  const now = Date.now();
  const windowStart = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY));
  const isFreshWindow = !windowStart || now - windowStart > RELOAD_WINDOW_MS;
  const reloadsSoFar = isFreshWindow
    ? 0
    : Number(sessionStorage.getItem(CHUNK_RELOAD_COUNT_KEY));

  if (reloadsSoFar >= MAX_RELOADS_PER_WINDOW) {
    return false;
  }

  if (isFreshWindow) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  }
  sessionStorage.setItem(CHUNK_RELOAD_COUNT_KEY, String(reloadsSoFar + 1));
  window.location.reload();

  return true;
}

/**
 * Wraps React.lazy with automatic page reload on chunk load failure.
 *
 * After a new deployment Vite's hashed chunk filenames change. Users whose
 * browsers still reference old chunk URLs will get a "Failed to fetch
 * dynamically imported module" error. This wrapper detects that and triggers
 * a full page reload so the browser fetches the new entry-point and chunks.
 *
 * Also exposes a `.preload()` method so parent routes can warm the chunk
 * cache before the component is actually rendered.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): LazyWithPreload<T> {
  const Component = lazy(() =>
    importFn().catch((error: unknown) => {
      if (!isChunkError(error)) {
        throw error;
      }

      if (reloadOnChunkError()) {
        // Return a never-resolving promise so React.lazy stays in "loading"
        // state while the browser reloads — prevents the ErrorBoundary from
        // flashing before the reload completes.
        return new Promise<{ default: T }>(() => {});
      }

      // Reload budget exhausted and still failing — let the error propagate
      // to the ErrorBoundary.
      throw error;
    })
  ) as LazyWithPreload<T>;

  Component.preload = importFn;

  return Component;
}
